import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FlightIcon from '@mui/icons-material/Flight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const GOLD = '#b79a59';
const NAVY = '#1c1e2a';
const GAP = 24; // px between cards
const EDGE = 12; // px of track padding so card shadows aren't clipped

function ReviewCard({ review }) {
  return (
    <Paper
      elevation={3}
      sx={{ height: '100%', p: 2.5, borderRadius: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            flex: '0 0 auto',
            borderRadius: 1.5,
            bgcolor: NAVY,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FlightIcon sx={{ transform: 'rotate(90deg)' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontStyle: 'italic', lineHeight: 1.2 }}>
            {review.name}
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {review.location}
          </Typography>
        </Box>
      </Box>

      <Typography sx={{ fontStyle: 'italic', overflowWrap: 'anywhere' }}>
        “{review.text}”
      </Typography>
    </Paper>
  );
}

// Customer reviews on an LO's landing page, lowest rank first. Shows 3 cards
// on desktop, 2 on tablet and 1 on mobile. Arrows (and swiping) appear only
// when there are more reviews than fit; each arrow click moves one card and
// stops at either end. Fewer reviews than fit are centered with no arrows.
export default function ReviewCarousel({ reviews }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'));
  const perView = isDesktop ? 3 : isTablet ? 2 : 1;

  const sorted = [...(reviews || [])].sort((a, b) => a.rank - b.rank);
  const scrollable = sorted.length > perView;

  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEnds = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateEnds();
  }, [updateEnds, perView, sorted.length]);

  const step = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.firstElementChild;
    const distance = card ? card.getBoundingClientRect().width + GAP : el.clientWidth;
    el.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  if (sorted.length === 0) return null;

  const arrowSx = { color: GOLD, p: { xs: 0.25, sm: 1 } };
  const cardWidth = `calc((100% - ${(perView - 1) * GAP}px) / ${perView})`;

  return (
    <Box
      role="region"
      aria-label="Customer reviews"
      sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 } }}
    >
      {scrollable && (
        <IconButton aria-label="Previous review" onClick={() => step(-1)} disabled={atStart} sx={arrowSx}>
          <ChevronLeftIcon sx={{ fontSize: { xs: 32, sm: 44 } }} />
        </IconButton>
      )}

      <Box
        ref={trackRef}
        onScroll={updateEnds}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          gap: `${GAP}px`,
          p: `${EDGE}px`,
          justifyContent: scrollable ? 'flex-start' : 'center',
          overflowX: scrollable ? 'auto' : 'visible',
          scrollSnapType: 'x mandatory',
          scrollPaddingInline: `${EDGE}px`,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {sorted.map((review, i) => (
          <Box
            key={review._id || i}
            sx={{ flex: `0 0 ${cardWidth}`, minWidth: 0, scrollSnapAlign: 'start' }}
          >
            <ReviewCard review={review} />
          </Box>
        ))}
      </Box>

      {scrollable && (
        <IconButton aria-label="Next review" onClick={() => step(1)} disabled={atEnd} sx={arrowSx}>
          <ChevronRightIcon sx={{ fontSize: { xs: 32, sm: 44 } }} />
        </IconButton>
      )}
    </Box>
  );
}
